import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { FaEye, FaPrint, FaFileExcel, FaFilePdf } from 'react-icons/fa';
import { getBalanceSheet, clearBalanceSheet, clearError, clearSuccess } from '../../../../redux/Slices/AccountingReportsSlice';
import { useTitle } from '../../../../shared/hooks/TitleContext';
import {
  generatePdfFromElementIds,
  waitForRender,
} from '../../../../shared/pdf/pdfEngine';
import * as XLSX from 'xlsx';
import i18n from '../../../../i18n/i18n';
import PdfPreviewModal from './components/PdfPreviewModal';
import { calculatePdfPagination } from '../../../../shared/utils.js/utils';
import { convertArabicNumbersToEnglish } from '../../../../shared/utils.js/utils';

const BalanceSheet = () => {
  const dispatch = useDispatch();
  const { setTitle } = useTitle();
  const { t } = useTranslation('global');
  const isRTL = i18n.language === 'ar';
  const textAlign = isRTL ? 'right' : 'left';
  const dir = isRTL ? 'rtl' : 'ltr';
  const publicUrl = process.env.PUBLIC_URL || '';
  const headerLogo = `${publicUrl}/ACE.png`;

  const { balanceSheet, isLoading, error, success } = useSelector((state) => state.accountingReports);

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [pdfType, setPdfType] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [pdfChunks, setPdfChunks] = useState([]); // For paginated PDF generation
  const [isFilterApplied, setIsFilterApplied] = useState(false);

  useEffect(() => {
    setTitle('accountingReports.balanceSheetTitle', { defaultValue: 'الميزانية' });
    dispatch(getBalanceSheet({}));
    return () => {
      dispatch(clearBalanceSheet());
    };
  }, [dispatch, setTitle]);

  useEffect(() => {
    if (success) {
      if (isFilterApplied) {
        toast.success(success, { rtl: isRTL });
      }
      dispatch(clearSuccess());
      setIsFilterApplied(false);
    }
  }, [success, dispatch, isRTL, isFilterApplied]);

  useEffect(() => {
    if (error) {
      toast.error(error, { rtl: isRTL });
      setTimeout(() => dispatch(clearError()), 2500);
    }
  }, [error, dispatch, isRTL]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsFilterApplied(true);
    const params = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    dispatch(getBalanceSheet(params));
  };

  const buildDynamicPdfChunks = (sectionName, sectionItems) => {
    const topHeader = document.getElementById(`balance-sheet-pdf-measure-${sectionName}-top-header`);
    const thead = document.getElementById(`balance-sheet-pdf-measure-${sectionName}-thead`);
    const footer = document.getElementById('balance-sheet-pdf-measure-footer');
    const tbody = document.getElementById(`balance-sheet-pdf-measure-${sectionName}-tbody`);
    return calculatePdfPagination(sectionItems, { topHeader, thead, footer, tbody }, { safeMargin: 25 });
  };

  const handlePreviewAll = () => {
    if (!balanceSheet) {
      toast.error(t('accountingReports.noData', { defaultValue: 'لا توجد بيانات للمعاينة' }), { rtl: isRTL });
      return;
    }
    const chunks = preparePdfChunks();
    setPdfChunks(chunks);
    setIsPreviewOpen(true);
  };

  const preparePdfChunks = () => {
    const pages = [];
    const assetsChunks = buildDynamicPdfChunks('assets', assets);
    const liabilitiesChunks = buildDynamicPdfChunks('liabilities', liabilities);
    const equityChunks = buildDynamicPdfChunks('equity', equity);

    const maxLiabilitiesEquityPages = Math.max(liabilitiesChunks.length, equityChunks.length);
    const liabilitiesEquityChunks = [];

    for (let i = 0; i < maxLiabilitiesEquityPages; i++) {
      const chunk = [];
      const liabilitiesChunk = liabilitiesChunks[i];
      const equityChunk = equityChunks[i];

      if (liabilitiesChunk && liabilitiesChunk.items.length > 0 && i === 0 && liabilities.length > 0) {
        chunk.push({ type: 'liabilitiesHeader' });
      }
      if (liabilitiesChunk && liabilitiesChunk.items.length > 0) {
        liabilitiesChunk.items.forEach((item) => {
          chunk.push({ type: 'liability', data: item });
        });
      }
      if (equityChunk && equityChunk.items.length > 0 && i === 0 && equity.length > 0) {
        chunk.push({ type: 'equityHeader' });
      }
      if (equityChunk && equityChunk.items.length > 0) {
        equityChunk.items.forEach((item) => {
          chunk.push({ type: 'equity', data: item });
        });
      }
      liabilitiesEquityChunks.push(chunk);
    }

    if (liabilitiesEquityChunks.length === 0) {
      liabilitiesEquityChunks.push([]);
    }

    const maxPages = Math.max(assetsChunks.length, liabilitiesEquityChunks.length);

    for (let pageIndex = 0; pageIndex < maxPages; pageIndex++) {
      const pageAssets = (assetsChunks[pageIndex] && assetsChunks[pageIndex].items) || [];
      const pageLiabilitiesEquity = liabilitiesEquityChunks[pageIndex] || [];
      const isLastPage = pageIndex === maxPages - 1;

      const pageLiabilities = [];
      const pageEquity = [];
      let hasLiabilitiesHeader = false;
      let hasEquityHeader = false;

      pageLiabilitiesEquity.forEach((item) => {
        if (item.type === 'liabilitiesHeader') hasLiabilitiesHeader = true;
        else if (item.type === 'liability') pageLiabilities.push(item.data);
        else if (item.type === 'equityHeader') hasEquityHeader = true;
        else if (item.type === 'equity') pageEquity.push(item.data);
      });

      const allAssetsProcessed = pageIndex === assetsChunks.length - 1 && pageAssets.length > 0;
      const allLiabilitiesProcessed = pageIndex === liabilitiesEquityChunks.length - 1 && pageLiabilities.length > 0 && liabilities.length > 0;
      const allEquityProcessed = pageIndex === liabilitiesEquityChunks.length - 1 && pageEquity.length > 0 && equity.length > 0;

      pages.push({
        pageNumber: pageIndex + 1,
        assets: pageAssets,
        liabilities: pageLiabilities,
        equity: pageEquity,
        hasLiabilitiesHeader,
        hasEquityHeader,
        showTotalAssets: allAssetsProcessed,
        showTotalLiabilities: allLiabilitiesProcessed,
        showTotalEquity: allEquityProcessed,
        showFinalTotal: isLastPage && (liabilities.length > 0 || equity.length > 0),
        totalAssets: allAssetsProcessed ? totalAssets : null,
        totalLiabilities: allLiabilitiesProcessed ? totalLiabilities : null,
        totalEquity: allEquityProcessed ? totalEquity : null,
        totalLiabilitiesAndEquity: isLastPage && (liabilities.length > 0 || equity.length > 0) ? totalLiabilitiesAndEquity : null
      });
    }

    return pages;
  };

  // Print all data in one PDF
  const handlePrintAll = async () => {
    if (!balanceSheet) {
      toast.error(t('accountingReports.noData', { defaultValue: 'لا توجد بيانات للطباعة' }), { rtl: isRTL });
      return;
    }

    try {
      setPdfType('all');
      
      const chunks = preparePdfChunks();
      setPdfChunks(chunks);
      
      await new Promise((resolve) => requestAnimationFrame(resolve));
      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready;
      }
      await new Promise((resolve) => setTimeout(resolve, 300));
      setIsGeneratingPdf(true);
                                                        
      await waitForRender(300);

      // Shared PDF Engine — capture + assembly (one A4 page per chunk)
      const pageElementIds = chunks.map((_, i) =>
        document.getElementById(`balance-sheet-pdf-all-${i}`)
          ? `balance-sheet-pdf-all-${i}`
          : `preview-balance-sheet-pdf-all-${i}`,
      );
      await generatePdfFromElementIds({
        elementIds: pageElementIds,
        fileName: `ACE_BalanceSheet_${new Date().toISOString().split('T')[0]}.pdf`,
        isRTL,
      });
      toast.success(t('accountingReports.printed', { defaultValue: 'تم طباعة التقرير بنجاح' }), { rtl: isRTL });
    } catch (error) {
      toast.error(t('accountingReports.printError', { defaultValue: 'حدث خطأ أثناء إنشاء PDF' }), { rtl: isRTL });
    } finally {
      setIsGeneratingPdf(false);
      setPdfType(null);
      setPdfChunks([]);
    }
  };

  const handleExportAllToExcel = () => {
    if (!balanceSheet) {
      toast.error(t('accountingReports.noData', { defaultValue: 'لا توجد بيانات للتصدير' }), { rtl: isRTL });
      return;
    }

    try {
      const workbook = XLSX.utils.book_new();
      
      // Assets Sheet
      const assetsData = assets.map(item => ({
        [t('accountingReports.code', { defaultValue: 'الكود' })]: item.code || '',
        [t('accountingReports.name', { defaultValue: 'الاسم' })]: isRTL ? (item.name_ar || item.name_en || '') : (item.name_en || item.name_ar || ''),
        [t('accountingReports.balance', { defaultValue: 'الرصيد' })]: item.balance || 0
      }));
      if (assets.length > 0) {
        assetsData.push({
          [t('accountingReports.code', { defaultValue: 'الكود' })]: '',
          [t('accountingReports.name', { defaultValue: 'الاسم' })]: t('accountingReports.totalAssets', { defaultValue: 'إجمالي الأصول' }),
          [t('accountingReports.balance', { defaultValue: 'الرصيد' })]: totalAssets
        });
      }
      const assetsSheet = XLSX.utils.json_to_sheet(assetsData);
      XLSX.utils.book_append_sheet(workbook, assetsSheet, t('accountingReports.assets', { defaultValue: 'الأصول' }));

      // Liabilities Sheet
      const liabilitiesData = liabilities.map(item => ({
        [t('accountingReports.code', { defaultValue: 'الكود' })]: item.code || '',
        [t('accountingReports.name', { defaultValue: 'الاسم' })]: isRTL ? (item.name_ar || item.name_en || '') : (item.name_en || item.name_ar || ''),
        [t('accountingReports.balance', { defaultValue: 'الرصيد' })]: item.balance || 0
      }));
      if (liabilities.length > 0) {
        liabilitiesData.push({
          [t('accountingReports.code', { defaultValue: 'الكود' })]: '',
          [t('accountingReports.name', { defaultValue: 'الاسم' })]: t('accountingReports.totalLiabilities', { defaultValue: 'إجمالي الخصوم' }),
          [t('accountingReports.balance', { defaultValue: 'الرصيد' })]: totalLiabilities
        });
      }
      const liabilitiesSheet = XLSX.utils.json_to_sheet(liabilitiesData);
      XLSX.utils.book_append_sheet(workbook, liabilitiesSheet, t('accountingReports.liabilities', { defaultValue: 'الخصوم' }));

      // Equity Sheet
      const equityData = equity.map(item => ({
        [t('accountingReports.code', { defaultValue: 'الكود' })]: item.code || '',
        [t('accountingReports.name', { defaultValue: 'الاسم' })]: isRTL ? (item.name_ar || item.name_en || '') : (item.name_en || item.name_ar || ''),
        [t('accountingReports.balance', { defaultValue: 'الرصيد' })]: item.balance || 0
      }));
      if (equity.length > 0) {
        equityData.push({
          [t('accountingReports.code', { defaultValue: 'الكود' })]: '',
          [t('accountingReports.name', { defaultValue: 'الاسم' })]: t('accountingReports.totalEquity', { defaultValue: 'إجمالي حقوق الملكية' }),
          [t('accountingReports.balance', { defaultValue: 'الرصيد' })]: totalEquity
        });
      }
      const equitySheet = XLSX.utils.json_to_sheet(equityData);
      XLSX.utils.book_append_sheet(workbook, equitySheet, t('accountingReports.equity', { defaultValue: 'حقوق الملكية' }));

      // Summary Sheet
      const summaryData = [
        { [t('accountingReports.item', { defaultValue: 'البند' })]: t('accountingReports.totalAssets', { defaultValue: 'إجمالي الأصول' }), [t('accountingReports.amount', { defaultValue: 'المبلغ' })]: totalAssets },
        { [t('accountingReports.item', { defaultValue: 'البند' })]: t('accountingReports.totalLiabilities', { defaultValue: 'إجمالي الخصوم' }), [t('accountingReports.amount', { defaultValue: 'المبلغ' })]: totalLiabilities },
        { [t('accountingReports.item', { defaultValue: 'البند' })]: t('accountingReports.totalEquity', { defaultValue: 'إجمالي حقوق الملكية' }), [t('accountingReports.amount', { defaultValue: 'المبلغ' })]: totalEquity },
        { [t('accountingReports.item', { defaultValue: 'البند' })]: t('accountingReports.totalLiabilitiesAndEquity', { defaultValue: 'إجمالي الخصوم وحقوق الملكية' }), [t('accountingReports.amount', { defaultValue: 'المبلغ' })]: totalLiabilitiesAndEquity }
      ];
      const summarySheet = XLSX.utils.json_to_sheet(summaryData);
      XLSX.utils.book_append_sheet(workbook, summarySheet, t('accountingReports.summary', { defaultValue: 'ملخص' }));

      XLSX.writeFile(workbook, `BalanceSheet_${new Date().toISOString().split('T')[0]}.xlsx`);
      toast.success(t('accountingReports.exported', { defaultValue: 'تم تصدير البيانات بنجاح' }), { rtl: isRTL });
    } catch (error) {
      toast.error(error.message || t('accountingReports.exportError', { defaultValue: 'فشل في تصدير البيانات' }), { rtl: isRTL });
    }
  };

  const formatNumber = (value) =>
    convertArabicNumbersToEnglish(
      Number(value ?? 0).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    );

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return convertArabicNumbersToEnglish(
      new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      })
    );
  };

  const reportData = balanceSheet?.data || balanceSheet || {};
  const assets = reportData.assets || [];
  const liabilities = reportData.liabilities || [];
  const equity = reportData.equity || [];
  const totalAssets = reportData.total_assets || 0;
  const totalLiabilities = reportData.total_liabilities || 0;
  const totalEquity = reportData.total_equity || 0;
  const totalLiabilitiesAndEquity = reportData.total_liabilities_and_equity || 0;



  return (
    <div style={{ padding: '20px', direction: isRTL ? 'rtl' : 'ltr' }}>
      <div style={{
        backgroundColor: '#1b3350',
        borderRadius: '12px',
        padding: '24px',
        border: '1px solid #333'
      }}>
        <h2 style={{ color: 'white', marginBottom: '20px', direction: dir, textAlign }}>
          {t('accountingReports.balanceSheetTitle', { defaultValue: 'الميزانية' })}
        </h2>

        {/* Filters */}
        <form onSubmit={handleSubmit} style={{
          backgroundColor: '#1a1f2e',
          padding: '20px',
          borderRadius: '8px',
          marginBottom: '20px'
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginBottom: '15px' }}>
            <div>
              <label style={{ color: '#ccc', fontSize: '14px', display: 'block', marginBottom: '8px', direction: dir, textAlign }}>
                {t('accountingReports.startDate', { defaultValue: 'تاريخ البداية' })}
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: '#14263b',
                  border: '1px solid #333',
                  borderRadius: '6px',
                  color: 'white',
                  fontSize: '16px'
                }}
              />
            </div>
            <div>
              <label style={{ color: '#ccc', fontSize: '14px', display: 'block', marginBottom: '8px', direction: dir, textAlign }}>
                {t('accountingReports.endDate', { defaultValue: 'تاريخ النهاية' })}
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: '#14263b',
                  border: '1px solid #333',
                  borderRadius: '6px',
                  color: 'white',
                  fontSize: '16px'
                }}
              />
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: '10px' }}>
            <button
              type="submit"
              disabled={isLoading}
              style={{
                backgroundColor: isLoading ? '#666' : '#AC2000',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '8px',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                fontSize: '16px',
                opacity: isLoading ? 0.6 : 1,
                minWidth: '180px',
                textAlign: 'center',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {isLoading ? t('accountingReports.loading', { defaultValue: 'جاري التحميل...' }) : t('accountingReports.view', { defaultValue: 'عرض' })}
            </button>
          </div>
        </form>

        {/* Action Buttons */}
        {balanceSheet && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginBottom: '20px', flexWrap: 'wrap' }}>
            <button
              onClick={handlePreviewAll}
              style={{
                backgroundColor: '#444',
                color: 'white',
                border: 'none',
                padding: '12px 20px',
                borderRadius: 8,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                justifyContent: 'center'
              }}
            >
              <FaEye /> {t('accountingReports.previewAllPDF', { defaultValue: 'عرض PDF للكل' })}
            </button>
            <button
              onClick={handlePrintAll}
              disabled={isGeneratingPdf}
              style={{
                backgroundColor: isGeneratingPdf ? '#666' : '#17a2b8',
                color: 'white',
                border: 'none',
                padding: '12px 20px',
                borderRadius: 8,
                cursor: isGeneratingPdf ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                justifyContent: 'center',
                opacity: isGeneratingPdf ? 0.6 : 1
              }}
            >
              <FaPrint /> {isGeneratingPdf ? t('accountingReports.generating', { defaultValue: 'جاري التوليد...' }) : t('accountingReports.printAllPDF', { defaultValue: 'طباعة PDF للكل' })}
            </button>
            <button
              onClick={handleExportAllToExcel}
              style={{
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                padding: '12px 20px',
                borderRadius: 8,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                justifyContent: 'center'
              }}
            >
              <FaFileExcel /> {t('accountingReports.exportExcel', { defaultValue: 'تصدير Excel للكل' })}
            </button>
          </div>
        )}

        {/* Report Data Display */}
        {balanceSheet && (
          <div style={{
            backgroundColor: '#1a1f2e',
            padding: '20px',
            borderRadius: '8px',
            marginTop: '20px'
          }}>
            <div style={{ color: '#ccc', marginBottom: '15px' }}>
              <div><strong>{t('accountingReports.date', { defaultValue: 'التاريخ' })}:</strong> {startDate && endDate ? `${formatDate(startDate)} - ${formatDate(endDate)}` : formatDate(endDate)}</div>
            </div>

            {/* Balance Sheet Table */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginTop: '20px' }}>
              {/* Assets Column */}
              <div>
                <h3 style={{ color: '#fff', marginBottom: '15px', fontSize: '18px', fontWeight: 'bold', direction: dir, textAlign }}>
                  {t('accountingReports.assets', { defaultValue: 'الأصول' })}
                </h3>
                <div style={{ border: '1px solid #444', borderRadius: '6px', overflow: 'hidden', direction: dir }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px', direction: dir }}>
                    <thead>
                      <tr style={{ backgroundColor: '#2a3441' }}>
                        <th style={{ border: '1px solid #444', padding: '10px', textAlign, color: '#fff' }}>
                          {t('accountingReports.code', { defaultValue: 'الكود' })}
                        </th>
                        <th style={{ border: '1px solid #444', padding: '10px', textAlign, color: '#fff' }}>
                          {t('accountingReports.name', { defaultValue: 'الاسم' })}
                        </th>
                        <th style={{ border: '1px solid #444', padding: '10px', textAlign: 'center', color: '#fff' }}>
                          {t('accountingReports.balance', { defaultValue: 'الرصيد' })}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {assets.length > 0 ? (
                        assets.map((item, idx) => (
                          <tr key={item.id || idx} style={{ backgroundColor: idx % 2 === 0 ? '#1a1f2e' : '#222831' }}>
                            <td style={{ border: '1px solid #444', padding: '8px', textAlign, color: '#ccc' }}>
                              {item.code || '—'}
                            </td>
                            <td style={{ border: '1px solid #444', padding: '8px', textAlign, color: '#ccc' }}>
                              {isRTL ? (item.name_ar || item.name_en || '—') : (item.name_en || item.name_ar || '—')}
                            </td>
                            <td style={{ border: '1px solid #444', padding: '8px', textAlign: 'center', color: '#ccc', fontWeight: 'bold' }}>
                              {formatNumber(item.balance || 0)}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="3" style={{ border: '1px solid #444', padding: '10px', textAlign: 'center', color: '#999' }}>
                            {t('accountingReports.noData', { defaultValue: 'لا توجد بيانات' })}
                          </td>
                        </tr>
                      )}
                      {assets.length > 0 && (
                        <tr style={{ backgroundColor: '#2a3441', fontWeight: 'bold' }}>
                          <td colSpan="2" style={{ border: '1px solid #444', padding: '10px', textAlign, color: '#fff' }}>
                            {t('accountingReports.totalAssets', { defaultValue: 'إجمالي الأصول' })}
                          </td>
                          <td style={{ border: '1px solid #444', padding: '10px', textAlign: 'center', color: '#fff' }}>
                            {formatNumber(totalAssets)}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
          </div>
              </div>

              {/* Liabilities and Equity Column */}
              <div>
                <h3 style={{ color: '#fff', marginBottom: '15px', fontSize: '18px', fontWeight: 'bold', direction: dir, textAlign }}>
                  {t('accountingReports.liabilitiesAndEquity', { defaultValue: 'الخصوم وحقوق الملكية' })}
                </h3>
                <div style={{ border: '1px solid #444', borderRadius: '6px', overflow: 'hidden' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#2a3441' }}>
                        <th style={{ border: '1px solid #444', padding: '10px', textAlign, color: '#fff' }}>
                          {t('accountingReports.code', { defaultValue: 'الكود' })}
                        </th>
                        <th style={{ border: '1px solid #444', padding: '10px', textAlign, color: '#fff' }}>
                          {t('accountingReports.name', { defaultValue: 'الاسم' })}
                        </th>
                        <th style={{ border: '1px solid #444', padding: '10px', textAlign: 'center', color: '#fff' }}>
                          {t('accountingReports.balance', { defaultValue: 'الرصيد' })}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* Liabilities */}
                      {liabilities.length > 0 && (
                        <>
                          <tr>
                            <td colSpan="3" style={{ border: '1px solid #444', padding: '8px', textAlign, color: '#fff', backgroundColor: '#2a3441', fontWeight: 'bold' }}>
                              {t('accountingReports.liabilities', { defaultValue: 'الخصوم' })}
                            </td>
                          </tr>
                          {liabilities.map((item, idx) => (
                            <tr key={item.id || idx} style={{ backgroundColor: idx % 2 === 0 ? '#1a1f2e' : '#222831' }}>
                              <td style={{ border: '1px solid #444', padding: '8px', textAlign, direction: dir, color: '#ccc' }}>
                                {item.code || '—'}
                              </td>
                              <td style={{ border: '1px solid #444', padding: '8px', textAlign, direction: dir, color: '#ccc' }}>
                                {isRTL ? (item.name_ar || item.name_en || '—') : (item.name_en || item.name_ar || '—')}
                              </td>
                              <td style={{ border: '1px solid #444', padding: '8px', textAlign: 'center', color: '#ccc', fontWeight: 'bold' }}>
                                {formatNumber(item.balance || 0)}
                              </td>
                            </tr>
                          ))}
                          {liabilities.length > 0 && (
                            <tr style={{ backgroundColor: '#2a3441', fontWeight: 'bold' }}>
                              <td colSpan="2" style={{ border: '1px solid #444', padding: '10px', textAlign, color: '#fff' }}>
                                {t('accountingReports.totalLiabilities', { defaultValue: 'إجمالي الخصوم' })}
                              </td>
                              <td style={{ border: '1px solid #444', padding: '10px', textAlign: 'center', color: '#fff' }}>
                                {formatNumber(totalLiabilities)}
                              </td>
                            </tr>
                          )}
                        </>
                      )}

                      {/* Equity */}
                      {equity.length > 0 && (
                        <>
                          <tr>
                            <td colSpan="3" style={{ border: '1px solid #444', padding: '8px', textAlign, direction: dir, color: '#fff', backgroundColor: '#2a3441', fontWeight: 'bold' }}>
                              {t('accountingReports.equity', { defaultValue: 'حقوق الملكية' })}
                            </td>
                          </tr>
                          {equity.map((item, idx) => (
                            <tr key={item.id || idx} style={{ backgroundColor: idx % 2 === 0 ? '#1a1f2e' : '#222831' }}>
                              <td style={{ border: '1px solid #444', padding: '8px', textAlign, direction: dir, color: '#ccc' }}>
                                {item.code || '—'}
                              </td>
                              <td style={{ border: '1px solid #444', padding: '8px', textAlign, direction: dir, color: '#ccc' }}>
                                {isRTL ? (item.name_ar || item.name_en || '—') : (item.name_en || item.name_ar || '—')}
                              </td>
                              <td style={{ border: '1px solid #444', padding: '8px', textAlign: 'center', color: '#ccc', fontWeight: 'bold' }}>
                                {formatNumber(item.balance || 0)}
                              </td>
                            </tr>
                          ))}
                          {equity.length > 0 && (
                            <tr style={{ backgroundColor: '#2a3441', fontWeight: 'bold' }}>
                              <td colSpan="2" style={{ border: '1px solid #444', padding: '10px', textAlign, color: '#fff' }}>
                                {t('accountingReports.totalEquity', { defaultValue: 'إجمالي حقوق الملكية' })}
                              </td>
                              <td style={{ border: '1px solid #444', padding: '10px', textAlign: 'center', color: '#fff' }}>
                                {formatNumber(totalEquity)}
                              </td>
                            </tr>
                          )}
                        </>
                      )}

                      {liabilities.length === 0 && equity.length === 0 && (
                        <tr>
                          <td colSpan="3" style={{ border: '1px solid #444', padding: '10px', textAlign: 'center', color: '#999' }}>
                            {t('accountingReports.noData', { defaultValue: 'لا توجد بيانات' })}
                          </td>
                        </tr>
                      )}

                      {(liabilities.length > 0 || equity.length > 0) && (
                        <tr style={{ backgroundColor: '#3a4451', fontWeight: 'bold' }}>
                          <td colSpan="2" style={{ border: '1px solid #444', padding: '10px', textAlign, color: '#fff', fontSize: '16px' }}>
                            {t('accountingReports.totalLiabilitiesAndEquity', { defaultValue: 'إجمالي الخصوم وحقوق الملكية' })}
                          </td>
                          <td style={{ border: '1px solid #444', padding: '10px', textAlign: 'center', color: '#fff', fontSize: '16px' }}>
                            {formatNumber(totalLiabilitiesAndEquity)}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* PDF Print Templates */}
      {pdfType === 'all' && balanceSheet && pdfChunks && pdfChunks.length > 0 && (
        pdfChunks.map((page, chunkIndex) => (
          <div
          key={chunkIndex}
          id={`balance-sheet-pdf-all-${chunkIndex}`}
          aria-hidden="true"
          style={{
            position: 'absolute',
            left: '-9999px',
            top: 0,
            width: '794px',
            minHeight: '1123px',
            height: 'auto',
            backgroundColor: '#ffffff',
            color: '#000000',
            fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
            fontWeight: 'bold',
            padding: '40px',
            boxSizing: 'border-box',
            overflow: 'visible'
          }}
        >
          <div style={{
            border: '5px solid #000',
            padding: '3px',
            backgroundColor: '#ffffff',
            boxSizing: 'border-box',
            width: '100%',
            minHeight: '1043px',
            height: 'auto',
            overflow: 'visible'
          }}>
            <div style={{
              border: '2px solid #000',
              minHeight: '1033px',
              height: '1033px',
              padding: '18px 18px 10px',
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
              fontWeight: 'bold',
              WebkitFontSmoothing: 'antialiased',
              MozOsxFontSmoothing: 'grayscale',
              textRendering: 'optimizeLegibility',
              direction: isRTL ? 'rtl' : 'ltr',
              backgroundColor: '#ffffff',
              boxSizing: 'border-box',
              width: '100%',
              overflow: 'visible'
            }}>
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '100%',
                height: '600px',
                backgroundImage: `url(${headerLogo})`,
                backgroundSize: '95%',
                backgroundPosition: 'center center',
                backgroundRepeat: 'no-repeat',
                opacity: 0.1,
                zIndex: 0,
                pointerEvents: 'none'
              }} />
              <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%', minHeight: '100%' }}>
                <img
                  src={`${publicUrl}/HeaderAce.png`}
                  alt="Header"
                  style={{
                    width: '100%',
                    height: 'auto',
                    objectFit: 'contain',
                    marginBottom: '8px',
                    borderBottom: '4px double #000',
                    paddingBottom: '8px',
                    flexShrink: 0
                  }}
                  crossOrigin="anonymous"
                />

                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'visible', minHeight: 0, paddingBottom: '120px' }}>
                  <div style={{ marginTop: '12px', textAlign: 'center', marginBottom: '20px', direction: dir }}>
                    <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#000080', margin: 0, direction: dir }}>
                      {t('accountingReports.balanceSheetTitle', { defaultValue: 'الميزانية' })}
                    </h2>
                    <div style={{ fontSize: '14px', marginTop: '8px', direction: dir }}>
                      {startDate && endDate ? `${formatDate(startDate)} - ${formatDate(endDate)}` : formatDate(endDate)}
                    </div>
                  </div>

                  {/* Balance Sheet Content */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', fontSize: '12px' }}>
                    {/* Assets */}
                    <div>
                      <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '10px', color: '#000080', textAlign: 'center', direction: dir }}>
                        {t('accountingReports.assets', { defaultValue: 'الأصول' })}
                      </h3>
                      <div style={{ border: '1px solid #000', borderRadius: '4px', overflow: 'hidden' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                          <thead>
                            <tr>
                              <th style={{ border: '1px solid #000', padding: '6px', backgroundColor: '#e5e7eb', textAlign, direction: dir }}>
                                {t('accountingReports.code', { defaultValue: 'الكود' })}
                              </th>
                              <th style={{ border: '1px solid #000', padding: '6px', backgroundColor: '#e5e7eb', textAlign, direction: dir }}>
                                {t('accountingReports.name', { defaultValue: 'الاسم' })}
                              </th>
                              <th style={{ border: '1px solid #000', padding: '6px', backgroundColor: '#e5e7eb', textAlign: 'center' }}>
                                {t('accountingReports.balance', { defaultValue: 'الرصيد' })}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {page.assets && page.assets.length > 0 ? (
                              page.assets.map((item, idx) => (
                                <tr key={item.id || idx}>
                                  <td style={{ border: '1px solid #000', padding: '5px', textAlign, direction: dir }}>
                                    {item.code || '—'}
                                  </td>
                                  <td style={{ border: '1px solid #000', padding: '5px', textAlign, direction: dir }}>
                                    {isRTL ? (item.name_ar || item.name_en || '—') : (item.name_en || item.name_ar || '—')}
                                  </td>
                                  <td style={{ border: '1px solid #000', padding: '5px', textAlign: 'center', fontWeight: 'bold' }}>
                                    {formatNumber(item.balance || 0)}
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="3" style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>
                                  {t('accountingReports.noData', { defaultValue: 'لا توجد بيانات' })}
                                </td>
                              </tr>
                            )}
                            {page.showTotalAssets && page.totalAssets !== null && (
                              <tr style={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
                                <td colSpan="2" style={{ border: '1px solid #000', padding: '6px', textAlign, direction: dir }}>
                                  {t('accountingReports.totalAssets')}
                                </td>
                                <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>
                                  {formatNumber(page.totalAssets)}
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Liabilities and Equity */}
                    <div>
                      <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '10px', color: '#000080', textAlign: 'center' }}>
                        {t('accountingReports.liabilitiesAndEquity', { defaultValue: 'الخصوم وحقوق الملكية' })}
                      </h3>
                      <div style={{ border: '1px solid #000', borderRadius: '4px', overflow: 'hidden' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                          <thead>
                            <tr>
                              <th style={{ border: '1px solid #000', padding: '6px', backgroundColor: '#e5e7eb', textAlign, direction: dir }}>
                                {t('accountingReports.code', { defaultValue: 'الكود' })}
                              </th>
                              <th style={{ border: '1px solid #000', padding: '6px', backgroundColor: '#e5e7eb', textAlign, direction: dir }}>
                                {t('accountingReports.name', { defaultValue: 'الاسم' })}
                              </th>
                              <th style={{ border: '1px solid #000', padding: '6px', backgroundColor: '#e5e7eb', textAlign: 'center' }}>
                                {t('accountingReports.balance', { defaultValue: 'الرصيد' })}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {page.hasLiabilitiesHeader && (
                              <tr>
                                <td colSpan="3" style={{ border: '1px solid #000', padding: '6px', textAlign, direction: dir, backgroundColor: '#e5e7eb', fontWeight: 'bold' }}>
                                  {t('accountingReports.liabilities')}
                                </td>
                              </tr>
                            )}
                            {page.liabilities && page.liabilities.length > 0 ? (
                              page.liabilities.map((item, idx) => (
                                <tr key={item.id || idx}>
                                  <td style={{ border: '1px solid #000', padding: '5px', textAlign, direction: dir }}>
                                    {item.code || '—'}
                                  </td>
                                  <td style={{ border: '1px solid #000', padding: '5px', textAlign, direction: dir }}>
                                    {isRTL ? (item.name_ar || item.name_en || '—') : (item.name_en || item.name_ar || '—')}
                                  </td>
                                  <td style={{ border: '1px solid #000', padding: '5px', textAlign: 'center', fontWeight: 'bold' }}>
                                    {formatNumber(item.balance || 0)}
                                  </td>
                                </tr>
                              ))
                            ) : null}
                            {page.showTotalLiabilities && page.totalLiabilities !== null && (
                              <tr style={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
                                <td colSpan="2" style={{ border: '1px solid #000', padding: '6px', textAlign, direction: dir }}>
                                  {t('accountingReports.totalLiabilities')}
                                </td>
                                <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>
                                  {formatNumber(page.totalLiabilities)}
                                </td>
                              </tr>
                            )}
                            {page.hasEquityHeader && (
                              <tr>
                                <td colSpan="3" style={{ border: '1px solid #000', padding: '6px', textAlign, direction: dir, backgroundColor: '#e5e7eb', fontWeight: 'bold' }}>
                                  {t('accountingReports.equity')}
                                </td>
                              </tr>
                            )}
                            {page.equity && page.equity.length > 0 ? (
                              page.equity.map((item, idx) => (
                                <tr key={item.id || idx}>
                                  <td style={{ border: '1px solid #000', padding: '5px', textAlign, direction: dir }}>
                                    {item.code || '—'}
                                  </td>
                                  <td style={{ border: '1px solid #000', padding: '5px', textAlign, direction: dir }}>
                                    {isRTL ? (item.name_ar || item.name_en || '—') : (item.name_en || item.name_ar || '—')}
                                  </td>
                                  <td style={{ border: '1px solid #000', padding: '5px', textAlign: 'center', fontWeight: 'bold' }}>
                                    {formatNumber(item.balance || 0)}
                                  </td>
                                </tr>
                              ))
                            ) : null}
                            {page.showTotalEquity && page.totalEquity !== null && (
                              <tr style={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
                                <td colSpan="2" style={{ border: '1px solid #000', padding: '6px', textAlign, direction: dir }}>
                                  {t('accountingReports.totalEquity')}
                                </td>
                                <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>
                                  {formatNumber(page.totalEquity)}
                                </td>
                              </tr>
                            )}
                            {(!page.liabilities || page.liabilities.length === 0) && (!page.equity || page.equity.length === 0) && (
                              <tr>
                                <td colSpan="3" style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>
                                  {t('accountingReports.noData')}
                                </td>
                              </tr>
                            )}
                            {page.showFinalTotal && page.totalLiabilitiesAndEquity !== null && (
                              <tr style={{ backgroundColor: '#d1d5db', fontWeight: 'bold', fontSize: '13px' }}>
                                <td colSpan="2" style={{ border: '1px solid #000', padding: '8px', textAlign, direction: dir }}>
                                  {t('accountingReports.totalLiabilitiesAndEquity')}
                                </td>
                                <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}>
                                  {formatNumber(page.totalLiabilitiesAndEquity)}
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>

                <div style={{
                  position: 'absolute',
                  bottom: '10px',
                  left: '18px',
                  right: '18px',
                  flexShrink: 0
                }}>
                  <img
                    src={`${publicUrl}/FooterAce.png`}
                    alt="Footer"
                    style={{
                      width: '100%',
                      height: 'auto',
                      objectFit: 'contain',
                      borderTop: '4px double #000',
                      paddingTop: '8px',
                    }}
                    crossOrigin="anonymous"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        ))
      )}

      {/* Preview Modal */}
      <PdfPreviewModal
        isOpen={isPreviewOpen && !!balanceSheet}
        title={t('accountingReports.balanceSheetTitle', { defaultValue: 'الميزانية' })}
        onClose={() => setIsPreviewOpen(false)}
        onPrint={handlePrintAll}
        isGeneratingPdf={isGeneratingPdf}
        >
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
            {pdfChunks && pdfChunks.length > 0 && pdfChunks.map((page, chunkIndex) => (
              <div
                key={chunkIndex}
                id={`preview-balance-sheet-pdf-all-${chunkIndex}`}
                style={{
                  width: '794px',
                  minHeight: '1123px',
                  height: 'auto',
                  backgroundColor: '#ffffff',
                  color: '#000000',
                  fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
                  fontWeight: 'bold',
                  padding: '40px',
                  boxSizing: 'border-box'
                }}
              >
              <div
                style={{
                  border: '5px solid #000',
                  padding: '3px',
                  backgroundColor: '#ffffff',
                  boxSizing: 'border-box',
              width: '794px',
                  minHeight: '1123px',
                  height: 'auto',
                  overflow: 'visible'
            }}
          >
            <div
              style={{
                    border: '2px solid #000',
                    minHeight: '1033px',
                    height: '1033px',
                    padding: '18px 18px 10px',
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
                    fontWeight: 'bold',
                    WebkitFontSmoothing: 'antialiased',
                    MozOsxFontSmoothing: 'grayscale',
                    textRendering: 'optimizeLegibility',
                    direction: dir,
                    backgroundColor: '#ffffff',
                    boxSizing: 'border-box',
                    width: '100%',
                    overflow: 'visible'
              }}
            >
              <div
                style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: '100%',
                      height: '600px',
                      backgroundImage: `url(${headerLogo})`,
                      backgroundSize: '95%',
                      backgroundPosition: 'center center',
                      backgroundRepeat: 'no-repeat',
                      opacity: 0.1,
                      zIndex: 0,
                      pointerEvents: 'none'
                }}
              />
                    <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%', minHeight: '100%' }}>
                      <img
                        src={`${publicUrl}/HeaderAce.png`}
                        alt="Header"
                        style={{
                          width: '100%',
                          height: 'auto',
                          objectFit: 'contain',
                          marginBottom: '8px',
                          borderBottom: '4px double #000',
                          paddingBottom: '8px',
                          flexShrink: 0
                        }}
                        crossOrigin="anonymous"
                      />

                      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'visible', minHeight: 0, paddingBottom: '120px' }}>
                        <div style={{ marginTop: '12px', textAlign: 'center', marginBottom: '20px', direction: dir }}>
                          <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#000080', margin: 0, direction: dir }}>
                            {t('accountingReports.balanceSheetTitle', { defaultValue: 'الميزانية' })}
                          </h2>
                          <div style={{ fontSize: '14px', marginTop: '8px', direction: dir }}>
                            {startDate && endDate ? `${formatDate(startDate)} - ${formatDate(endDate)}` : formatDate(endDate)}
                          </div>
                        </div>

                        {/* Balance Sheet Content */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', fontSize: '12px' }}>
                          {/* Assets */}
                          <div>
                            <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '10px', color: '#000080', textAlign: 'center' }}>
                              {t('accountingReports.assets', { defaultValue: 'الأصول' })}
                            </h3>
                            <div style={{ border: '1px solid #000', borderRadius: '4px', overflow: 'hidden' }}>
                              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                                <thead>
                                  <tr>
                                    <th style={{ border: '1px solid #000', padding: '6px', backgroundColor: '#e5e7eb', textAlign, direction: dir }}>
                                      {t('accountingReports.code', { defaultValue: 'الكود' })}
                                    </th>
                                    <th style={{ border: '1px solid #000', padding: '6px', backgroundColor: '#e5e7eb', textAlign, direction: dir }}>
                                      {t('accountingReports.name', { defaultValue: 'الاسم' })}
                                    </th>
                                    <th style={{ border: '1px solid #000', padding: '6px', backgroundColor: '#e5e7eb', textAlign: 'center' }}>
                                      {t('accountingReports.balance', { defaultValue: 'الرصيد' })}
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {page.assets && page.assets.length > 0 ? (
                                    page.assets.map((item, idx) => (
                                      <tr key={item.id || idx}>
                                        <td style={{ border: '1px solid #000', padding: '5px', textAlign, direction: dir }}>
                                          {item.code || '—'}
                                        </td>
                                        <td style={{ border: '1px solid #000', padding: '5px', textAlign, direction: dir }}>
                                          {isRTL ? (item.name_ar || item.name_en || '—') : (item.name_en || item.name_ar || '—')}
                                        </td>
                                        <td style={{ border: '1px solid #000', padding: '5px', textAlign: 'center', fontWeight: 'bold' }}>
                                          {formatNumber(item.balance || 0)}
                                        </td>
                                      </tr>
                                    ))
                                  ) : (
                                    <tr>
                                      <td colSpan="3" style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>
                                        {t('accountingReports.noData')}
                                      </td>
                                    </tr>
                                  )}
                                  {page.showTotalAssets && page.totalAssets !== null && (
                                    <tr style={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
                                      <td colSpan="2" style={{ border: '1px solid #000', padding: '6px', textAlign, direction: dir }}>
                                        {t('accountingReports.totalAssets')}
                                      </td>
                                      <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>
                                        {formatNumber(page.totalAssets)}
                                      </td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                        </div>
                      </div>

                          {/* Liabilities and Equity */}
                          <div>
                            <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '10px', color: '#000080', textAlign: 'center' }}>
                              {t('accountingReports.liabilitiesAndEquity', { defaultValue: 'الخصوم وحقوق الملكية' })}
                            </h3>
                            <div style={{ border: '1px solid #000', borderRadius: '4px', overflow: 'hidden' }}>
                              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                                <thead>
                                  <tr>
                                    <th style={{ border: '1px solid #000', padding: '6px', backgroundColor: '#e5e7eb', textAlign, direction: dir }}>
                                      {t('accountingReports.code', { defaultValue: 'الكود' })}
                                    </th>
                                    <th style={{ border: '1px solid #000', padding: '6px', backgroundColor: '#e5e7eb', textAlign, direction: dir }}>
                                      {t('accountingReports.name', { defaultValue: 'الاسم' })}
                                    </th>
                                    <th style={{ border: '1px solid #000', padding: '6px', backgroundColor: '#e5e7eb', textAlign: 'center' }}>
                                      {t('accountingReports.balance', { defaultValue: 'الرصيد' })}
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {page.hasLiabilitiesHeader && (
                                    <tr>
                                      <td colSpan="3" style={{ border: '1px solid #000', padding: '6px', textAlign, direction: dir, backgroundColor: '#e5e7eb', fontWeight: 'bold' }}>
                                        {t('accountingReports.liabilities')}
                                      </td>
                                    </tr>
                                  )}
                                  {page.liabilities && page.liabilities.length > 0 ? (
                                    page.liabilities.map((item, idx) => (
                                      <tr key={item.id || idx}>
                                        <td style={{ border: '1px solid #000', padding: '5px', textAlign, direction: dir }}>
                                          {item.code || '—'}
                                        </td>
                                        <td style={{ border: '1px solid #000', padding: '5px', textAlign, direction: dir }}>
                                          {isRTL ? (item.name_ar || item.name_en || '—') : (item.name_en || item.name_ar || '—')}
                                        </td>
                                        <td style={{ border: '1px solid #000', padding: '5px', textAlign: 'center', fontWeight: 'bold' }}>
                                          {formatNumber(item.balance || 0)}
                                        </td>
                                      </tr>
                                    ))
                                  ) : null}
                                  {page.showTotalLiabilities && page.totalLiabilities !== null && (
                                    <tr style={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
                                      <td colSpan="2" style={{ border: '1px solid #000', padding: '6px', textAlign, direction: dir }}>
                                        {t('accountingReports.totalLiabilities')}
                                      </td>
                                      <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>
                                        {formatNumber(page.totalLiabilities)}
                                      </td>
                                    </tr>
                                  )}
                                  {page.hasEquityHeader && (
                                    <tr>
                                      <td colSpan="3" style={{ border: '1px solid #000', padding: '6px', textAlign, direction: dir, backgroundColor: '#e5e7eb', fontWeight: 'bold' }}>
                                        {t('accountingReports.equity')}
                                      </td>
                                    </tr>
                                  )}
                                  {page.equity && page.equity.length > 0 ? (
                                    page.equity.map((item, idx) => (
                                      <tr key={item.id || idx}>
                                        <td style={{ border: '1px solid #000', padding: '5px', textAlign, direction: dir }}>
                                          {item.code || '—'}
                                        </td>
                                        <td style={{ border: '1px solid #000', padding: '5px', textAlign, direction: dir }}>
                                          {isRTL ? (item.name_ar || item.name_en || '—') : (item.name_en || item.name_ar || '—')}
                                        </td>
                                        <td style={{ border: '1px solid #000', padding: '5px', textAlign: 'center', fontWeight: 'bold' }}>
                                          {formatNumber(item.balance || 0)}
                                        </td>
                                      </tr>
                                    ))
                                  ) : null}
                                  {page.showTotalEquity && page.totalEquity !== null && (
                                    <tr style={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
                                      <td colSpan="2" style={{ border: '1px solid #000', padding: '6px', textAlign, direction: dir }}>
                                        {t('accountingReports.totalEquity')}
                                      </td>
                                      <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>
                                        {formatNumber(page.totalEquity)}
                                      </td>
                                    </tr>
                                  )}
                                  {(!page.liabilities || page.liabilities.length === 0) && (!page.equity || page.equity.length === 0) && (
                                    <tr>
                                      <td colSpan="3" style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>
                                        {t('accountingReports.noData')}
                                      </td>
                                    </tr>
                                  )}
                                  {page.showFinalTotal && page.totalLiabilitiesAndEquity !== null && (
                                    <tr style={{ backgroundColor: '#d1d5db', fontWeight: 'bold', fontSize: '13px' }}>
                                      <td colSpan="2" style={{ border: '1px solid #000', padding: '8px', textAlign, direction: dir }}>
                                        {t('accountingReports.totalLiabilitiesAndEquity')}
                                      </td>
                                      <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}>
                                        {formatNumber(page.totalLiabilitiesAndEquity)}
                                      </td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      </div>

                <div
                  style={{
                        position: 'absolute',
                        bottom: '10px',
                        left: '18px',
                        right: '18px',
                        flexShrink: 0
                  }}
                >
                        <img
                          src={`${publicUrl}/FooterAce.png`}
                          alt="Footer"
                          style={{
                            width: '100%',
                            height: 'auto',
                            objectFit: 'contain',
                            borderTop: '4px double #000',
                            paddingTop: '8px',
                          }}
                          crossOrigin="anonymous"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
      </PdfPreviewModal>

      {/* Measurement template for dynamic PDF pagination */}
      <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', top: 0, width: '794px', pointerEvents: 'none', visibility: 'hidden' }}>
        <div id="balance-sheet-pdf-measure-assets-top-header" style={{ width: '100%' }}>
          <img src={`${publicUrl}/HeaderAce.png`} alt="" style={{ width: '100%', height: 'auto', objectFit: 'contain', marginBottom: '8px', borderBottom: '4px double #000', paddingBottom: '8px' }} />
          <div style={{ marginTop: '12px', textAlign: 'center', marginBottom: '20px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: '700', margin: 0 }}>{t('accountingReports.balanceSheetTitle', { defaultValue: 'الميزانية' })}</h2>
            <div style={{ fontSize: '14px', marginTop: '8px' }}>{startDate && endDate ? `${formatDate(startDate)} - ${formatDate(endDate)}` : formatDate(endDate)}</div>
          </div>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #000' }}>
          <thead id="balance-sheet-pdf-measure-assets-thead">
            <tr>
              <th style={{ border: '1px solid #000', padding: '6px' }}>{t('accountingReports.code', { defaultValue: 'الكود' })}</th>
              <th style={{ border: '1px solid #000', padding: '6px' }}>{t('accountingReports.name', { defaultValue: 'الاسم' })}</th>
              <th style={{ border: '1px solid #000', padding: '6px' }}>{t('accountingReports.balance', { defaultValue: 'الرصيد' })}</th>
            </tr>
          </thead>
          <tbody id="balance-sheet-pdf-measure-assets-tbody">
            {assets.slice(0, 5).map((item, idx) => (
              <tr key={idx}>
                <td style={{ border: '1px solid #000', padding: '6px' }}>{item.code || '—'}</td>
                <td style={{ border: '1px solid #000', padding: '6px' }}>{isRTL ? (item.name_ar || item.name_en || '—') : (item.name_en || item.name_ar || '—')}</td>
                <td style={{ border: '1px solid #000', padding: '6px' }}>{formatNumber(item.balance || 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div id="balance-sheet-pdf-measure-liabilities-top-header" style={{ width: '100%' }}>
          <img src={`${publicUrl}/HeaderAce.png`} alt="" style={{ width: '100%', height: 'auto', objectFit: 'contain', marginBottom: '8px', borderBottom: '4px double #000', paddingBottom: '8px' }} />
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #000' }}>
          <thead id="balance-sheet-pdf-measure-liabilities-thead">
            <tr>
              <th style={{ border: '1px solid #000', padding: '6px' }}>{t('accountingReports.code', { defaultValue: 'الكود' })}</th>
              <th style={{ border: '1px solid #000', padding: '6px' }}>{t('accountingReports.name', { defaultValue: 'الاسم' })}</th>
              <th style={{ border: '1px solid #000', padding: '6px' }}>{t('accountingReports.balance', { defaultValue: 'الرصيد' })}</th>
            </tr>
          </thead>
          <tbody id="balance-sheet-pdf-measure-liabilities-tbody">
            {liabilities.slice(0, 5).map((item, idx) => (
              <tr key={idx}>
                <td style={{ border: '1px solid #000', padding: '6px' }}>{item.code || '—'}</td>
                <td style={{ border: '1px solid #000', padding: '6px' }}>{isRTL ? (item.name_ar || item.name_en || '—') : (item.name_en || item.name_ar || '—')}</td>
                <td style={{ border: '1px solid #000', padding: '6px' }}>{formatNumber(item.balance || 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div id="balance-sheet-pdf-measure-equity-top-header" style={{ width: '100%' }} />
        <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #000' }}>
          <thead id="balance-sheet-pdf-measure-equity-thead">
            <tr>
              <th style={{ border: '1px solid #000', padding: '6px' }}>{t('accountingReports.code', { defaultValue: 'الكود' })}</th>
              <th style={{ border: '1px solid #000', padding: '6px' }}>{t('accountingReports.name', { defaultValue: 'الاسم' })}</th>
              <th style={{ border: '1px solid #000', padding: '6px' }}>{t('accountingReports.balance', { defaultValue: 'الرصيد' })}</th>
            </tr>
          </thead>
          <tbody id="balance-sheet-pdf-measure-equity-tbody">
            {equity.slice(0, 5).map((item, idx) => (
              <tr key={idx}>
                <td style={{ border: '1px solid #000', padding: '6px' }}>{item.code || '—'}</td>
                <td style={{ border: '1px solid #000', padding: '6px' }}>{isRTL ? (item.name_ar || item.name_en || '—') : (item.name_en || item.name_ar || '—')}</td>
                <td style={{ border: '1px solid #000', padding: '6px' }}>{formatNumber(item.balance || 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div id="balance-sheet-pdf-measure-footer" style={{ height: '120px' }} />
      </div>
    </div>
  );
};

export default BalanceSheet;
